import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  MenuItem,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Tooltip,
  Menu,
  CssBaseline,
} from '@mui/material';
import {
  Add,
  Search as SearchIcon,
  FilterList,
  MoreVert,
  CheckCircleOutline,
  CancelOutlined,
  EditOutlined,
  DoNotDisturbAltOutlined,
  HandshakeOutlined,
  WarningAmberOutlined,
  EventAvailableOutlined,
  PaidOutlined,
} from '@mui/icons-material';
import AppTheme from '../../../../shared-theme/AppTheme';
import SideMenu from '../../../../components/SideMenu';
import Header from '../../../../components/Header';
import { StyledCellHeader } from '../../../../components/StyledCellHeader';
import { formatNumberWithCommas } from '../../../../utils/generalFunctions';
import CrearPromesaPagoDialog from './crearpromesapagodialog';
import { ESTADO_LABELS, ESTADO_COLORS, computedEstado } from './mockdata';
import AlertMessageDialog from '../../../../components/Dialogs/alertMessageDialog';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Usuario-ID': localStorage.getItem('id'),
  'X-company-ID': localStorage.getItem('idcompany'),
});

const formatFecha = (isoString) => {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (isNaN(d)) return '—';
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}/${d.getUTCFullYear()}`;
};

const MetricCard = ({ label, value, accent, icon: Icon }) => (
  <Card
    variant="outlined"
    sx={{
      borderRadius: 2,
      border: '1px solid',
      borderColor: 'divider',
      bgcolor: 'background.paper',
      height: '100%',
      position: 'relative',
      overflow: 'hidden',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: 3,
        height: '100%',
        bgcolor: accent,
      },
    }}
  >
    <CardContent sx={{ py: 2, px: 2.5, pl: 3, '&:last-child': { pb: 2 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75 }}>
            {label}
          </Typography>
          <Typography fontWeight={700} sx={{ fontSize: '1.25rem', letterSpacing: '-0.01em', lineHeight: 1.2, color: accent }}>
            {value}
          </Typography>
        </Box>
        {Icon && <Icon sx={{ color: accent, fontSize: 28, opacity: 0.8 }} />}
      </Stack>
    </CardContent>
  </Card>
);

export default function PromesasPago() {
  const [promesas, setPromesas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const [menuPromesa, setMenuPromesa] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'success' });

  const fetchPromesas = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago/${localStorage.getItem('idcompany')}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      setPromesas(Array.isArray(data) ? data : []);
    } catch (err) {
      setPromesas([]);
      setAlert({ open: true, message: 'Error al cargar promesas', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPromesas(); }, [fetchPromesas]);

  const promesasConEstado = useMemo(
    () => promesas.map((p) => ({
      ...p,
      monto_prometido: parseFloat(p.monto_prometido) || 0,
      monto_pagado: parseFloat(p.monto_pagado) || 0,
      estadoComputed: computedEstado(p),
    })),
    [promesas],
  );

  const filteredPromesas = useMemo(() => {
    return promesasConEstado.filter((p) => {
      if (estadoFilter && p.estadoComputed !== estadoFilter) return false;
      if (search) {
        const term = search.toLowerCase();
        return (
          (p.loan_id || '').toLowerCase().includes(term) ||
          (p.cliente_nombre || '').toLowerCase().includes(term) ||
          (p.cliente_identificacion || '').toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [promesasConEstado, estadoFilter, search]);

  const metrics = useMemo(() => {
    const pendientes = promesasConEstado.filter((p) => p.estadoComputed === 'pendiente');
    const vencidas = promesasConEstado.filter((p) => p.estadoComputed === 'vencida');
    const cumplidas = promesasConEstado.filter((p) => p.estadoComputed === 'cumplida');
    const montoPendiente = pendientes.reduce((acc, p) => acc + p.monto_prometido, 0)
      + vencidas.reduce((acc, p) => acc + (p.monto_prometido - p.monto_pagado), 0);
    return {
      pendientes: pendientes.length,
      vencidas: vencidas.length,
      cumplidas: cumplidas.length,
      montoPendiente,
    };
  }, [promesasConEstado]);

  const handleSavePromesa = async () => {
    setAlert({ open: true, message: editing ? 'Promesa actualizada' : 'Promesa registrada', severity: 'success' });
    setEditing(null);
    await fetchPromesas();
  };

  const openMenu = (event, promesa) => {
    setMenuEl(event.currentTarget);
    setMenuPromesa(promesa);
  };

  const closeMenu = () => {
    setMenuEl(null);
    setMenuPromesa(null);
  };

  const handleMarcarEstado = async (estado) => {
    if (!menuPromesa) return;
    try {
      const body = { estado };
      if (estado === 'cumplida') body.monto_pagado = parseFloat(menuPromesa.monto_prometido);
      const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/promesas_pago/${menuPromesa.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error('Error al actualizar la promesa');
      setAlert({ open: true, message: `Promesa marcada como ${ESTADO_LABELS[estado].toLowerCase()}`, severity: 'success' });
      await fetchPromesas();
    } catch (err) {
      setAlert({ open: true, message: err.message, severity: 'error' });
    } finally {
      closeMenu();
    }
  };

  const handleEdit = () => {
    setEditing(menuPromesa);
    setDialogOpen(true);
    closeMenu();
  };

  return (
    <Box>
      <AppTheme themeComponents={{}}>
        <CssBaseline enableColorScheme />
        <Box sx={{ display: 'flex' }}>
          <SideMenu />
          <Box component="main" sx={{ flexGrow: 1, overflow: 'auto', bgcolor: 'background.default', minHeight: '100vh' }}>
            <Stack sx={{ alignItems: 'stretch', pb: 6, mt: { xs: 8, md: 0 } }}>
              <Header parent="Gestión Comercial" child="Promesas de Pago" />

              <Box sx={{ px: { xs: 2, md: 4 }, pt: 3 }}>
                {/* Page header */}
                <Box display="flex" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={2} mb={3}>
                  <Box>
                    <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">Promesas de Pago</Typography>
                    <Typography variant="body2" color="text.secondary" mt={0.25}>
                      Gestioná los compromisos de pago acordados con los clientes
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primarybutton"
                    startIcon={<Add />}
                    onClick={() => { setEditing(null); setDialogOpen(true); }}
                    sx={{ fontWeight: 700, textTransform: 'none' }}
                  >
                    Nueva promesa
                  </Button>
                </Box>

                {/* Metric cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard label="Pendientes" value={metrics.pendientes} accent="#ed6c02" icon={HandshakeOutlined} />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard label="Vencidas" value={metrics.vencidas} accent="#c62828" icon={WarningAmberOutlined} />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard label="Cumplidas" value={metrics.cumplidas} accent="#2e7d32" icon={EventAvailableOutlined} />
                  </Grid>
                  <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                    <MetricCard label="Monto pendiente" value={`RD$${formatNumberWithCommas(metrics.montoPendiente.toFixed(2))}`} accent="#18586b" icon={PaidOutlined} />
                  </Grid>
                </Grid>

                {/* Filters */}
                <Card variant="outlined" sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, boxShadow: 'none' }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Grid container spacing={2}>
                      <Grid item size={{ xs: 12, md: 7 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Buscar por préstamo, cliente o cédula"
                          value={search}
                          onChange={(e) => setSearch(e.target.value)}
                          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
                        />
                      </Grid>
                      <Grid item size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          size="small"
                          select
                          label="Estado"
                          value={estadoFilter}
                          onChange={(e) => setEstadoFilter(e.target.value)}
                        >
                          <MenuItem value="">Todos</MenuItem>
                          <MenuItem value="pendiente">Pendiente</MenuItem>
                          <MenuItem value="vencida">Vencida</MenuItem>
                          <MenuItem value="cumplida">Cumplida</MenuItem>
                          <MenuItem value="incumplida">Incumplida</MenuItem>
                          <MenuItem value="cancelada">Cancelada</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item size={{ xs: 12, md: 2 }}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<FilterList />}
                          onClick={() => { setSearch(''); setEstadoFilter(''); }}
                          sx={{ height: 40, fontWeight: 600, textTransform: 'none' }}
                        >
                          Limpiar
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Table */}
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, boxShadow: 'none' }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledCellHeader>Préstamo</StyledCellHeader>
                        <StyledCellHeader>Cliente</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'center' }}>Fecha promesa</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'right' }}>Monto</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'right' }}>Pagado</StyledCellHeader>
                        <StyledCellHeader sx={{ textAlign: 'center' }}>Estado</StyledCellHeader>
                        <StyledCellHeader>Registrada por</StyledCellHeader>
                        <StyledCellHeader sx={{ width: 60 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                            Cargando promesas…
                          </TableCell>
                        </TableRow>
                      )}
                      {!loading && filteredPromesas.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} sx={{ textAlign: 'center', py: 5, color: 'text.secondary' }}>
                            No hay promesas que coincidan con los filtros.
                          </TableCell>
                        </TableRow>
                      )}
                      {filteredPromesas.map((p) => (
                        <TableRow key={p.id} hover>
                          <TableCell><Typography variant="body2" fontWeight={600}>{p.loan_id}</Typography></TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>{p.cliente_nombre}</Typography>
                            <Typography variant="caption" color="text.secondary">{p.cliente_identificacion}</Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>{formatFecha(p.fecha_promesa)}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>RD${formatNumberWithCommas(p.monto_prometido.toFixed(2))}</TableCell>
                          <TableCell sx={{ textAlign: 'right' }}>RD${formatNumberWithCommas((p.monto_pagado || 0).toFixed(2))}</TableCell>
                          <TableCell sx={{ textAlign: 'center' }}>
                            <Chip
                              size="small"
                              label={ESTADO_LABELS[p.estadoComputed]}
                              color={ESTADO_COLORS[p.estadoComputed]}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{p.created_by_nombre || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatFecha(p.created_at)}</Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Acciones">
                              <IconButton size="small" onClick={(e) => openMenu(e, p)}>
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Stack>
          </Box>
        </Box>

        <Menu
          anchorEl={menuEl}
          open={Boolean(menuEl)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { borderRadius: 2, minWidth: 200, boxShadow: '0 4px 20px rgba(0,0,0,0.12)' } } }}
        >
          <MenuItem onClick={handleEdit}>
            <EditOutlined sx={{ mr: 1.5, fontSize: 18, color: 'primary.main' }} />
            <Typography variant="body2" fontWeight={600}>Editar</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMarcarEstado('cumplida')}>
            <CheckCircleOutline sx={{ mr: 1.5, fontSize: 18, color: 'success.main' }} />
            <Typography variant="body2" fontWeight={600}>Marcar cumplida</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMarcarEstado('incumplida')}>
            <CancelOutlined sx={{ mr: 1.5, fontSize: 18, color: 'error.main' }} />
            <Typography variant="body2" fontWeight={600}>Marcar incumplida</Typography>
          </MenuItem>
          <MenuItem onClick={() => handleMarcarEstado('cancelada')}>
            <DoNotDisturbAltOutlined sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={600}>Cancelar promesa</Typography>
          </MenuItem>
        </Menu>

        <CrearPromesaPagoDialog
          open={dialogOpen}
          initial={editing}
          onClose={() => { setDialogOpen(false); setEditing(null); }}
          onSave={handleSavePromesa}
        />

        <AlertMessageDialog
          open={alert.open}
          handleClose={() => setAlert({ ...alert, open: false })}
          title=""
          message={alert.message}
          severity={alert.severity}
          color={alert.severity}
        />
      </AppTheme>
    </Box>
  );
}
